import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Switch,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { NotificationCategories } from '@wwaave/shared';
import { useEffect, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import {
  type Digest,
  DigestSections,
} from '../../components/notifications/DigestSections';
import { MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { csrfHeaders } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

// Source the category list from the shared package — categories.ts is the
// single source of truth. Each new category added in shared (e.g. recap,
// tour_route, open_week, network) automatically surfaces here without an
// edit. Unknown labels fall through to a sensible default so the page never
// crashes on a fresh category that lands before this UI catches up.
const CATEGORIES = NotificationCategories.NOTIFICATION_CATEGORIES;
type Category = (typeof CATEGORIES)[number];

// Two channels surfaced in the UI: in-app + email. web_push is server-side
// stubbed and not yet toggleable from settings.
const CHANNELS = ['in_app', 'email'] as const;
type Channel = (typeof CHANNELS)[number];

const CATEGORY_LABELS: Record<Category, { title: string; help: string }> = {
  account: { title: 'Account', help: 'Sign-in and security alerts.' },
  verification: { title: 'Verification', help: 'When your verification status changes.' },
  exchange: { title: 'Exchange', help: 'Requests, confirmations, and messages.' },
  review: { title: 'Review', help: 'When reviews are submitted or released.' },
  recap: { title: 'Recaps', help: 'Co-authored recaps from your completed exchanges.' },
  dispute: { title: 'Dispute', help: 'Updates on raised concerns.' },
  subscription: { title: 'Subscription', help: 'Trial, billing, and plan changes.' },
  event: { title: 'Events', help: 'When events at venues you watch are published.' },
  event_interest: { title: 'Event interest', help: 'When a host accepts or declines your interest in an event.' },
  tour_route: { title: 'Tour route matches', help: 'When a venue or open week fits a saved route.' },
  open_week: { title: 'Open week matches', help: 'When a host posts an open week that fits one of your routes.' },
  network: { title: 'Network Notes digests', help: 'Per-channel delivery for the digest. Cadence is set below.' },
  founder_letter: { title: 'Notes from the road', help: 'Quarterly note from the founder.' },
  marketing: { title: 'Product news', help: 'New features, opt-in only.' },
  report: { title: 'Reports', help: 'Updates on reports you’ve filed or, if you’re on the team, new reports in the queue.' },
  referral: { title: 'Referrals', help: 'When someone joins through your link, and when they reach a payable milestone.' },
};

type PrefRow = {
  category: Category;
  channel: 'in_app' | 'email' | 'web_push';
  enabled: boolean;
  required: boolean;
};

type PreferencesResponse = {
  ok: boolean;
  preferences: PrefRow[];
};

type DigestCadenceResponse = {
  ok: boolean;
  weekly: boolean;
  daily: boolean;
};

const PREFS_KEY = ['notifications', 'preferences'] as const;
const DIGEST_KEY = ['notifications', 'digest-cadence'] as const;

export const Route = createFileRoute('/settings/notifications')({
  component: NotificationSettingsPage,
});

type StateMap = Record<Category, Record<Channel, { enabled: boolean; required: boolean }>>;

function emptyState(): StateMap {
  const out = {} as StateMap;
  for (const c of CATEGORIES) {
    out[c] = {
      in_app: { enabled: false, required: false },
      email: { enabled: false, required: false },
    };
  }
  return out;
}

function NotificationSettingsPage() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<StateMap>(emptyState());
  const [dirty, setDirty] = useState(false);

  const query = useQuery({
    queryKey: PREFS_KEY,
    queryFn: async (): Promise<PreferencesResponse> => {
      const res = await fetch('/api/notifications/preferences', {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load preferences');
      return res.json();
    },
  });

  const cadenceQuery = useQuery({
    queryKey: DIGEST_KEY,
    queryFn: async (): Promise<DigestCadenceResponse> => {
      const res = await fetch('/api/notifications/digest-cadence', {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load digest cadence');
      return res.json();
    },
  });

  useEffect(() => {
    if (!query.data) return;
    const next = emptyState();
    for (const p of query.data.preferences) {
      if (p.channel === 'web_push') continue;
      if (!(p.category in next)) continue;
      next[p.category][p.channel] = { enabled: p.enabled, required: p.required };
    }
    setState(next);
    setDirty(false);
  }, [query.data]);

  const save = useMutation({
    mutationFn: async () => {
      const preferences = CATEGORIES.flatMap((category) =>
        CHANNELS.map((channel) => ({
          category,
          channel,
          enabled: state[category][channel].enabled,
        })),
      );
      const res = await fetch('/api/notifications/preferences', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ preferences }),
      });
      if (!res.ok) throw new Error('Save failed');
    },
    onSuccess: () => {
      showToast({ kind: 'success', body: 'Preferences saved.' });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: PREFS_KEY });
    },
    onError: () => showToast({ kind: 'error', body: 'Save failed. Try again shortly.' }),
  });

  const cadenceMutation = useMutation({
    mutationFn: async (input: { weekly?: boolean; daily?: boolean }) => {
      const res = await fetch('/api/notifications/digest-cadence', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Save failed');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DIGEST_KEY });
    },
    onError: () => showToast({ kind: 'error', body: "Couldn't update digest cadence." }),
  });

  if (query.isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  const cadence = cadenceQuery.data;

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Notifications"
            lede="Choose where we contact you. Email is on for everything important."
          />
        </MotionSection>

        <SettingsTabs current="notifications" />

        <Card withBorder padding={0} radius="md">
          <Table.ScrollContainer minWidth={420}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Category</Table.Th>
                  <Table.Th style={{ width: 100 }}>In-app</Table.Th>
                  <Table.Th style={{ width: 100 }}>Email</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {CATEGORIES.map((cat) => {
                  const meta = CATEGORY_LABELS[cat] ?? {
                    title: cat,
                    help: 'New category. Defaults apply until you set them.',
                  };
                  return (
                    <Table.Tr key={cat}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text fw={600}>{meta.title}</Text>
                          <Text size="xs" c="dimmed">
                            {meta.help}
                          </Text>
                        </Stack>
                      </Table.Td>
                      {CHANNELS.map((ch) => {
                        const cell = state[cat][ch];
                        return (
                          <Table.Td key={ch}>
                            <Switch
                              checked={cell.enabled}
                              disabled={cell.required}
                              label={cell.required ? 'Always on' : undefined}
                              aria-label={`${meta.title}: ${ch === 'in_app' ? 'In-app' : 'Email'}`}
                              onChange={(e) => {
                                const next = { ...state };
                                next[cat] = {
                                  ...next[cat],
                                  [ch]: { ...cell, enabled: e.currentTarget.checked },
                                };
                                setState(next);
                                setDirty(true);
                              }}
                            />
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>

        <Group justify="flex-end">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            Save preferences
          </Button>
        </Group>

        <MotionSection>
          <SectionTitle title="Network Notes digest" />
          <Card withBorder padding="lg" radius="md" mt="sm">
            <Stack gap="md">
              <Text c="dimmed" size="sm">
                A short read on what moved across the network. New venues, fresh recaps, open weeks
                that fit a route you saved, and what's on your calendar. The weekly note arrives
                Monday morning; the daily one is opt-in for closer attention.
              </Text>
              <DigestCadenceRow
                label="Weekly digest"
                description="Mondays at 9 in your inbox. Default on."
                window="weekly"
                checked={cadence?.weekly ?? true}
                disabled={cadenceQuery.isLoading || cadenceMutation.isPending}
                onToggle={(v) => cadenceMutation.mutate({ weekly: v })}
              />
              <DigestCadenceRow
                label="Daily digest"
                description="A short morning note, every day. Default off."
                window="daily"
                checked={cadence?.daily ?? false}
                disabled={cadenceQuery.isLoading || cadenceMutation.isPending}
                onToggle={(v) => cadenceMutation.mutate({ daily: v })}
              />
              <Text size="xs" c="dimmed">
                Email transport isn't wired yet. Digests land in <strong>/notifications</strong>.
                Use Preview to see exactly what would be sent right now.
              </Text>
            </Stack>
          </Card>
        </MotionSection>
      </Stack>
    </AppShell>
  );
}

/**
 * One cadence row — switch on the left, Preview button on the right. The
 * Preview button opens a modal that fetches the live digest payload and
 * renders it via the shared DigestSections component (the exact JSX the
 * inbox card uses for delivered digests).
 */
function DigestCadenceRow({
  label,
  description,
  window,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  description: string;
  window: 'daily' | 'weekly';
  checked: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}) {
  const [opened, { open, close }] = useDisclosure(false);
  return (
    <Box>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Switch
          size="md"
          label={label}
          description={description}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.currentTarget.checked)}
          style={{ flex: 1 }}
        />
        <Button variant="subtle" size="xs" onClick={open}>
          Preview
        </Button>
      </Group>
      <DigestPreviewModal opened={opened} onClose={close} window={window} />
    </Box>
  );
}

function DigestPreviewModal({
  opened,
  onClose,
  window,
}: {
  opened: boolean;
  onClose: () => void;
  window: 'daily' | 'weekly';
}) {
  const preview = useQuery({
    queryKey: ['notifications', 'digest-preview', window],
    queryFn: async (): Promise<{ ok: boolean; digest: Digest }> => {
      const res = await fetch(`/api/notifications/digest/preview?window=${window}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load preview');
      return res.json();
    },
    enabled: opened,
    // Refresh each open — the underlying data changes as the network does.
    staleTime: 0,
    gcTime: 0,
  });

  const eyebrow = window === 'weekly' ? 'Weekly Network Notes' : 'Daily Network Notes';
  const title =
    window === 'weekly'
      ? 'Network Notes: your week on WWAAVE'
      : 'Network Notes: today on WWAAVE';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        <Stack gap={2}>
          <Text size="xs" tt="uppercase" c="sun" fw={700} style={{ letterSpacing: '0.08em' }}>
            {eyebrow} · Preview
          </Text>
          <Text fw={700} size="lg">
            {title}
          </Text>
        </Stack>
      }
      radius="md"
      centered
    >
      <Stack gap="sm">
        {preview.isLoading && <Loader size="sm" />}
        {preview.isError && (
          <Text size="sm" c="red">
            Couldn't build the preview. Try again shortly.
          </Text>
        )}
        {preview.data && (
          <>
            <Group gap="xs">
              <Badge color="sun" variant="light" size="xs">
                Live preview
              </Badge>
              {preview.data.digest.isEmpty && (
                <Badge color="gray" variant="light" size="xs">
                  Nothing to send right now
                </Badge>
              )}
            </Group>
            <DigestSections digest={preview.data.digest} />
            <Text size="xs" c="dimmed" mt="xs">
              This is the exact body that would land in your inbox at the next{' '}
              {window === 'weekly' ? 'Monday 9am' : '9am'} (Brisbane). Empty digests are
              suppressed at send time.
            </Text>
          </>
        )}
      </Stack>
    </Modal>
  );
}
